import React, { useState } from 'react'

import Alert from 'components_new/atoms/Alert'
import Button from 'components_new/atoms/Button'
import Icon from 'components_new/atoms/Icon'
import List from 'components_new/atoms/List'
import ListItem from 'components_new/atoms/List/ListItem'
import ListItemText from 'components_new/atoms/List/ListItemText'

import CollapsibleSection from 'components_new/molecules/CollapsibleSection'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import CreateAllocationKeyDialog from 'components_new/organisms/dialogs/CreateAllocationKeyDialog'
import EditAllocationKeyDialog from 'components_new/organisms/dialogs/EditAllocationKeyDialog'

import { IAllocationKey } from 'types/GlobalAllocationKey'

import { useAllocationKeyActions } from 'redux/actions/AllocationKeys'
import { useAllocationKeysData } from 'redux/hooks/AllocationKeys'
import { useTranslation } from 'language/translator'

const AllocationKeys = () => {
  const allocationKeysData = useAllocationKeysData()
  const { tryDeleteAllocationKey } = useAllocationKeyActions()

  const { t } = useTranslation()

  const [allocationKeyToDelete, setAllocationKeyToDelete] =
    useState<IAllocationKey | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [allocationKeyToUpdate, setAllocationKeyToUpdate] = useState<
    string | null
  >(null)

  const handleOpenCreate = () => {
    setCreateOpen(true)
  }

  const handleCloseCreate = () => {
    setCreateOpen(false)
  }

  const handleCloseUpdate = () => {
    setAllocationKeyToUpdate(null)
  }

  const handleCloseDelete = () => {
    setAllocationKeyToDelete(null)
  }

  return (
    <>
      <CollapsibleSection
        title={t('global', 'allocation_keys')}
        sx={{ flexGrow: 1, maxWidth: 'section' }}
        actions={
          <Button
            onClick={handleOpenCreate}
            startIcon={<Icon name="AddCircleOutlineOutlined" />}
          >
            {t('global', 'add')}
          </Button>
        }
      >
        <Alert severity="info" icon={<Icon name="Info" />}>
          {t('allocation_keys', 'about')}
        </Alert>

        <List>
          {Object.values(allocationKeysData).map((allocationKey) => (
            <ListItem
              key={allocationKey.id}
              secondaryAction={
                <>
                  <Button
                    onClick={() => setAllocationKeyToUpdate(allocationKey.id)}
                    variant="text"
                  >
                    {t('global', 'edit')}
                  </Button>
                  <Button
                    onClick={() => setAllocationKeyToDelete(allocationKey)}
                    variant="text"
                  >
                    {t('global', 'delete')}
                  </Button>
                </>
              }
            >
              <Icon color="disabled" name="VpnKeyOutlined" />
              <ListItemText
                sx={{ ml: 3 }}
                primary={allocationKey.name}
                primaryTypographyProps={{ fontWeight: 500 }}
                secondary={`${t('allocation_keys', 'distributes')} ${
                  allocationKey.accounts.length
                } ${t(
                  'global',
                  allocationKey.accounts.length === 1 ? 'account' : 'accounts',
                  true
                )}`}
              />
            </ListItem>
          ))}
        </List>
      </CollapsibleSection>
      <SimpleDialog
        title={'Radera fördelningsnyckel?'}
        open={Boolean(allocationKeyToDelete)}
        onClose={handleCloseDelete}
        actions={
          <>
            <Button variant="text" onClick={handleCloseDelete}>
              {t('global', 'close')}
            </Button>
            <Button
              color="error"
              loading={isDeleting}
              onClick={() => {
                setIsDeleting(true)

                tryDeleteAllocationKey(
                  (allocationKeyToDelete as IAllocationKey).id,
                  (err) => {
                    if (!err) {
                      handleCloseDelete()
                    }

                    setIsDeleting(false)
                  }
                )
              }}
              variant="contained"
            >
              {t('global', 'delete')}
            </Button>
          </>
        }
        contentText={
          <>
            Fördelningsnyckeln <code>{allocationKeyToDelete?.name}</code> kommer
            att raderas permanent.{' '}
            <b>
              Utfall utan koppling till en fastighet via konstadsställen kommer
              att försvinna från konton som fördelas via nyckeln.
            </b>{' '}
            Är du säker?
          </>
        }
      />
      <CreateAllocationKeyDialog
        onClose={handleCloseCreate}
        open={createOpen}
      />
      <EditAllocationKeyDialog
        allocationKeyId={allocationKeyToUpdate}
        onClose={handleCloseUpdate}
      />
    </>
  )
}

export default AllocationKeys
